import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Plus, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function OccasionsPage() {
  const navigate = useNavigate();
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/occasions/public")
      .then(({ data }) => setOccasions(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const applyTemplate = (o) => {
    navigate("/", {
      state: {
        template: {
          source_occasion_id: o.id,
          title: o.title,
          description: o.description,
          questions: o.questions,
        },
      },
    });
  };

  const createOwn = () => {
    navigate("/", {
      state: { template: { source_occasion_id: null, title: "", description: "", questions: [""] } },
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <nav className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2 text-[#C86D51]">
            <Gift className="w-6 h-6" />
            <span className="text-sm font-semibold tracking-wide uppercase">Portwrite</span>
          </Link>
        </nav>

        <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-[#2C221E]">{t("occasions.title")}</h1>
        <p className="mt-4 text-base sm:text-lg text-[#7A6B63] leading-relaxed">
          {t("occasions.subtitle")}
        </p>

        {loading ? (
          <div className="mt-10 text-[#7A6B63]">{t("common.loading")}</div>
        ) : (
          <div className="mt-8 space-y-5" data-testid="occasions-list">
            {occasions.map((o, i) => (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 * i }}
                className="rounded-3xl border border-[#E8DEC2] bg-white p-6 shadow-sm"
                data-testid={`occasion-card-${o.id}`}
              >
                <div className="flex items-center gap-2 text-[#C86D51] mb-1">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">{t("occasions.templateBadge")}</span>
                </div>
                <h2 className="font-serif text-2xl text-[#2C221E]">{o.title}</h2>
                {o.description && <p className="mt-1 text-[#7A6B63]">{o.description}</p>}

                <ol className="mt-4 space-y-2 list-decimal list-inside">
                  {o.questions.map((q, idx) => (
                    <li key={idx} className="text-[15px] text-[#4A3E39] leading-relaxed">{q}</li>
                  ))}
                </ol>

                <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm text-[#7A6B63]" data-testid={`occasion-usage-${o.id}`}>
                    {t("occasions.usage", { n: o.usage_count })}
                  </span>
                  <Button
                    onClick={() => applyTemplate(o)}
                    className="h-11 px-5 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
                    data-testid={`use-occasion-${o.id}`}
                  >
                    {t("occasions.use")}
                  </Button>
                </div>
              </motion.div>
            ))}

            <Button
              onClick={createOwn}
              className="w-full h-14 text-base bg-[#2C221E] hover:bg-[#3d3029] text-white rounded-2xl"
              data-testid="create-own-occasion-button"
            >
              <Plus className="w-5 h-5 mr-2" /> {t("occasions.createOwn")}
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
